import React from "react";
import { useQuery } from "@tanstack/react-query";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { SectionHeader, PageLoader } from "@/components/Shared";
import { BadgeCard } from "@/components/achievements/BadgeCard";
import { CATEGORY_META, TIER_META, TIER_ORDER } from "@/lib/badges";
import { cn } from "@/lib/utils";

function TrophySummary({ badges, L }) {
  const earned = badges.filter((b) => b.earned).length;
  const pct = badges.length ? Math.round((earned / badges.length) * 100) : 0;
  return (
    <div className="rounded-3xl border bg-card p-6" data-testid="trophy-summary">
      <div className="flex items-center gap-4">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-yellow-600 via-amber-400 to-yellow-300 text-white shadow-lg"><Icons.Trophy className="h-7 w-7" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-muted-foreground">{L === "id" ? "Koleksi piala" : "Trophy collection"}</div>
          <div className="font-display text-2xl font-bold tabular-nums" data-testid="trophy-summary-count">{earned}/{badges.length}</div>
        </div>
        <span className="font-display text-xl font-bold tabular-nums text-primary">{pct}%</span>
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {TIER_ORDER.map((k) => {
          const tier = TIER_META[k];
          const all = badges.filter((b) => b.tier === k);
          return (
            <div key={k} className="flex items-center gap-2.5 rounded-xl border px-3 py-2.5" data-testid={`tier-count-${k}`}>
              <span className={cn("h-6 w-6 shrink-0 rounded-full bg-gradient-to-br", tier.grad)} />
              <div className="min-w-0">
                <div className={cn("text-[11px] font-bold uppercase tracking-wider", tier.text)}>{tier[L]}</div>
                <div className="text-sm font-semibold tabular-nums">{all.filter((b) => b.earned).length}/{all.length}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CategorySection({ cat, badges, L, lang }) {
  const Icon = Icons[cat.icon] || Icons.Trophy;
  if (!badges.length) return null;
  return (
    <section data-testid={`badge-category-${cat.key}`}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/15 text-primary"><Icon className="h-4 w-4" /></span>{cat[L]}
        </h2>
        <span className="text-sm tabular-nums text-muted-foreground">{badges.filter((b) => b.earned).length}/{badges.length}</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {badges.map((b) => <BadgeCard key={b.key} b={b} lang={lang} />)}
      </div>
    </section>
  );
}

export default function Achievements() {
  const { t, lang } = useI18n();
  const L = lang === "id" ? "id" : "en";
  const { data, isLoading } = useQuery({ queryKey: ["achievements"], queryFn: async () => (await api.get("/achievements")).data });

  if (isLoading) return <PageLoader />;

  const stats = [
    { label: L === "id" ? "Streak Check-in" : "Check-in Streak", value: data?.checkin_streak ?? 0, unit: t("days"), icon: Icons.CalendarCheck, accent: "bg-primary/15 text-primary" },
    { label: t("workoutStreak"), value: data?.workout_streak ?? 0, unit: t("days"), icon: Icons.Flame, accent: "bg-orange-500/15 text-orange-500" },
    { label: L === "id" ? "Total Check-in" : "Total Check-ins", value: data?.total_checkins ?? 0, unit: "", icon: Icons.BadgeCheck, accent: "bg-sky-500/15 text-sky-500" },
    { label: t("totalSessions"), value: data?.total_sessions ?? 0, unit: "", icon: Icons.Dumbbell, accent: "bg-warning/15 text-warning" },
  ];
  const badges = data?.badges || [];
  const earnedCount = badges.filter((b) => b.earned).length;

  return (
    <div className="space-y-10">
      <SectionHeader title={t("achievements")} subtitle={`${earnedCount}/${badges.length} ${t("earned")}`} />
      <TrophySummary badges={badges} L={L} />
      <div className="grid grid-cols-2 gap-5 lg:grid-cols-4" data-testid="achievement-stats">
        {stats.map((s) => (
          <div key={s.label} className="rounded-2xl border bg-card p-5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-muted-foreground">{s.label}</span>
              <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", s.accent)}><s.icon className="h-4 w-4" /></span>
            </div>
            <div className="mt-4 font-display text-3xl font-bold tabular-nums">{s.value}<span className="text-base font-normal text-muted-foreground"> {s.unit}</span></div>
          </div>
        ))}
      </div>
      {CATEGORY_META.map((cat) => (
        <CategorySection key={cat.key} cat={cat} L={L} lang={lang} badges={badges.filter((b) => b.category === cat.key)} />
      ))}
    </div>
  );
}
